import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import api from '../services/api';

// ── helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const STATUS_COLORS = {
  PENDING:   'bg-yellow-500/20 text-yellow-400',
  ACTIVE:    'bg-blue-500/20 text-blue-400',
  COMPLETED: 'bg-green-500/20 text-green-400',
  CANCELLED: 'bg-red-500/20 text-red-400',
  DISPUTED:  'bg-orange-500/20 text-orange-400',
};
const TYPE_COLORS = {
  ADMIN:    'bg-purple-500/20 text-purple-400',
  PROVIDER: 'bg-blue-500/20 text-blue-400',
  CLIENT:   'bg-gray-500/20 text-gray-400',
};
const CAT_LABELS = {
  MODELS: 'Models', VOICEOVER: 'Voiceover', DESIGN_BRANDING: 'Design', MARKETING_SOCIAL: 'Marketing',
  VIDEO_PHOTOGRAPHY: 'Video/Photo', WEB_DEVELOPMENT: 'Web Dev',
  CONTENT_CREATION: 'Content', STUDIO_RENTALS: 'Studio', EVENT_PLANNING: 'Events',
};

// ── sub-components ────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = '#6C5CE7' }) {
  return (
    <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
      <p className="text-dark-subtext text-xs mb-1">{label}</p>
      <p className="text-2xl font-bold text-dark-text" style={{ color }}>{value}</p>
      {sub && <p className="text-dark-subtext text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

function Avatar({ src, name, size = 32 }) {
  if (src) return <img src={src} alt={name} style={{ width: size, height: size }} className="rounded-full object-cover flex-shrink-0" />;
  const initials = name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
  return (
    <div style={{ width: size, height: size, background: '#6C5CE7', fontSize: size * 0.38 }}
      className="rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
      {initials}
    </div>
  );
}

function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-4">
      <button disabled={page === 1} onClick={() => onPage(page - 1)}
        className="px-3 py-1.5 rounded-lg bg-dark-surface border border-dark-border text-dark-subtext text-sm disabled:opacity-30">
        ←
      </button>
      <span className="text-dark-subtext text-sm">{page} / {pages}</span>
      <button disabled={page === pages} onClick={() => onPage(page + 1)}
        className="px-3 py-1.5 rounded-lg bg-dark-surface border border-dark-border text-dark-subtext text-sm disabled:opacity-30">
        →
      </button>
    </div>
  );
}

// ── MiniChart (sparkline svg) ─────────────────────────────────────────────────
function Sparkline({ data, color, label }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d[label]), 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 280;
    const y = 44 - (d[label] / max) * 40;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width="280" height="48" viewBox="0 0 280 48" className="w-full">
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts} />
      <polyline fill={`${color}22`} stroke="none"
        points={`0,44 ${pts} 280,44`} />
    </svg>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────
const TABS = ['Overview', 'Users', 'Bookings', 'Services', 'Reports', 'Verify', 'Waitlist'];

export default function AdminPage() {
  const { user } = useAuthStore();
  const { currency } = useCurrencyStore();
  const navigate = useNavigate();

  const [tab, setTab] = useState('Overview');

  // Overview
  const [stats, setStats]   = useState(null);
  const [growth, setGrowth] = useState([]);

  // Users
  const [users, setUsers]       = useState([]);
  const [uTotal, setUTotal]     = useState(0);
  const [uPage, setUPage]       = useState(1);
  const [uPages, setUPages]     = useState(1);
  const [uSearch, setUSearch]   = useState('');
  const [uType, setUType]       = useState('');
  const [uLoading, setULoading] = useState(false);

  // Bookings
  const [bookings, setBookings] = useState([]);
  const [bTotal, setBTotal]     = useState(0);
  const [bPage, setBPage]       = useState(1);
  const [bPages, setBPages]     = useState(1);
  const [bStatus, setBStatus]   = useState('');
  const [bLoading, setBLoading] = useState(false);

  // Services
  const [services, setSvcs]     = useState([]);
  const [sTotal, setSTotal]     = useState(0);
  const [sPage, setSPage]       = useState(1);
  const [sPages, setSPages]     = useState(1);
  const [sSearch, setSSearch]   = useState('');
  const [sLoading, setSLoading] = useState(false);

  // Reports
  const [reports, setReports]     = useState([]);
  const [rStatus, setRStatus]     = useState('PENDING');
  const [rLoading, setRLoading]   = useState(false);

  // Verifications
  const [verifs, setVerifs]       = useState([]);
  const [vStatus, setVStatus]     = useState('PENDING');
  const [vLoading, setVLoading]   = useState(false);

  // Waitlist
  const [wEntries, setWEntries]   = useState([]);
  const [wTotal, setWTotal]       = useState(0);
  const [wPage, setWPage]         = useState(1);
  const [wPages, setWPages]       = useState(1);
  const [wSearch, setWSearch]     = useState('');
  const [wType, setWType]         = useState('');
  const [wLoading, setWLoading]   = useState(false);

  // Guard: only ADMIN
  useEffect(() => {
    if (user && user.accountType !== 'ADMIN') navigate('/home', { replace: true });
  }, [user]);

  // Fetch overview on mount / tab switch
  useEffect(() => {
    if (tab !== 'Overview') return;
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/admin/growth').then(r => setGrowth(r.data)).catch(() => {});
  }, [tab]);

  const fetchUsers = useCallback(() => {
    setULoading(true);
    api.get('/admin/users', { params: { page: uPage, search: uSearch, accountType: uType } })
      .then(r => { setUsers(r.data.users); setUTotal(r.data.total); setUPages(r.data.pages); })
      .finally(() => setULoading(false));
  }, [uPage, uSearch, uType]);

  useEffect(() => { if (tab === 'Users') fetchUsers(); }, [tab, fetchUsers]);

  const fetchBookings = useCallback(() => {
    setBLoading(true);
    api.get('/admin/bookings', { params: { page: bPage, status: bStatus } })
      .then(r => { setBookings(r.data.bookings); setBTotal(r.data.total); setBPages(r.data.pages); })
      .finally(() => setBLoading(false));
  }, [bPage, bStatus]);

  useEffect(() => { if (tab === 'Bookings') fetchBookings(); }, [tab, fetchBookings]);

  const fetchServices = useCallback(() => {
    setSLoading(true);
    api.get('/admin/services', { params: { page: sPage, search: sSearch } })
      .then(r => { setSvcs(r.data.services); setSTotal(r.data.total); setSPages(r.data.pages); })
      .finally(() => setSLoading(false));
  }, [sPage, sSearch]);

  useEffect(() => { if (tab === 'Services') fetchServices(); }, [tab, fetchServices]);

  const toggleUserActive = async (u) => {
    try {
      const updated = await api.patch(`/admin/users/${u.id}`, { isActive: !u.isActive });
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, isActive: updated.data.isActive } : x));
    } catch {}
  };

  const deleteService = async (id) => {
    if (!window.confirm('Delete this service? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/services/${id}`);
      fetchServices();
    } catch {}
  };

  const toggleFeatured = async (s) => {
    try {
      const { data } = await api.patch(`/admin/services/${s.id}/featured`, { isFeatured: !s.isFeatured });
      setSvcs(prev => prev.map(x => x.id === s.id ? { ...x, isFeatured: data.isFeatured } : x));
    } catch {}
  };

  const resolveBooking = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      fetchBookings();
    } catch {}
  };

  const fetchReports = useCallback(() => {
    setRLoading(true);
    api.get('/admin/reports', { params: { status: rStatus } })
      .then(r => setReports(r.data))
      .finally(() => setRLoading(false));
  }, [rStatus]);

  useEffect(() => { if (tab === 'Reports') fetchReports(); }, [tab, fetchReports]);

  const resolveReport = async (id, status) => {
    try {
      await api.patch(`/admin/reports/${id}`, { status });
      fetchReports();
    } catch {}
  };

  const fetchVerifs = useCallback(() => {
    setVLoading(true);
    api.get('/admin/verifications', { params: { status: vStatus } })
      .then(r => setVerifs(r.data))
      .finally(() => setVLoading(false));
  }, [vStatus]);

  useEffect(() => { if (tab === 'Verify') fetchVerifs(); }, [tab, fetchVerifs]);

  const handleVerif = async (id, status) => {
    try {
      await api.patch(`/admin/verifications/${id}`, { status });
      fetchVerifs();
    } catch {}
  };

  const fetchWaitlist = useCallback(() => {
    setWLoading(true);
    api.get('/admin/waitlist', { params: { page: wPage, search: wSearch, type: wType } })
      .then(r => { setWEntries(r.data.entries); setWTotal(r.data.total); setWPages(r.data.pages); })
      .finally(() => setWLoading(false));
  }, [wPage, wSearch, wType]);

  useEffect(() => { if (tab === 'Waitlist') fetchWaitlist(); }, [tab, fetchWaitlist]);

  const removeFromWaitlist = async (id) => {
    try {
      await api.delete(`/admin/waitlist/${id}`);
      fetchWaitlist();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-dark-bg text-dark-text" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-dark-bg border-b border-dark-border px-4 pt-12 pb-0">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold" style={{ letterSpacing: '-0.5px' }}>Admin Dashboard</h1>
            <p className="text-dark-subtext text-xs">Findr control panel</p>
          </div>
          <span className="text-xs px-2.5 py-1 rounded-full bg-purple-500/20 text-purple-400 font-semibold">ADMIN</span>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-0 scrollbar-none">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
                tab === t ? 'bg-dark-surface text-dark-text border-t border-x border-dark-border' : 'text-dark-subtext'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-5 pb-24">

        {/* ── OVERVIEW ── */}
        {tab === 'Overview' && (
          <div className="space-y-5">
            {/* Stat grid */}
            <div className="grid grid-cols-2 gap-3">
              <StatCard label="Total Users" value={stats?.users.total ?? '…'} sub={`+${stats?.users.newThisMonth ?? 0} this month`} />
              <StatCard label="Providers" value={stats?.users.providers ?? '…'} color="#0984e3" />
              <StatCard label="Total Bookings" value={stats?.bookings.total ?? '…'} sub={`+${stats?.bookings.newThisMonth ?? 0} this month`} color="#00b894" />
              <StatCard label="Revenue" value={stats ? formatPrice(stats.revenue.total, currency) : '…'} color="#00b894" />
            </div>

            {/* Booking breakdown */}
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
              <p className="text-sm font-semibold mb-3">Booking Breakdown</p>
              <div className="space-y-2">
                {['pending','completed','cancelled','disputed'].map(key => {
                  const val = stats?.bookings[key] ?? 0;
                  const total = stats?.bookings.total || 1;
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-0.5">
                        <span className="capitalize text-dark-subtext">{key}</span>
                        <span className="text-dark-text font-medium">{val}</span>
                      </div>
                      <div className="h-1.5 bg-dark-border rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${(val / total) * 100}%`,
                            background: key === 'completed' ? '#00b894' : key === 'pending' ? '#fdcb6e' : key === 'disputed' ? '#e17055' : '#636e72',
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick stats row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-dark-surface border border-dark-border rounded-2xl p-3 text-center">
                <p className="text-lg font-bold text-dark-text">{stats?.services.active ?? '…'}</p>
                <p className="text-dark-subtext text-[11px]">Active Services</p>
              </div>
              <div className="bg-dark-surface border border-dark-border rounded-2xl p-3 text-center">
                <p className="text-lg font-bold text-dark-text">{stats?.messages.total ?? '…'}</p>
                <p className="text-dark-subtext text-[11px]">Messages</p>
              </div>
              <div className="bg-dark-surface border border-dark-border rounded-2xl p-3 text-center">
                <p className="text-lg font-bold text-dark-text">{stats?.reviews.total ?? '…'}</p>
                <p className="text-dark-subtext text-[11px]">Reviews</p>
              </div>
            </div>

            {/* Growth chart */}
            {growth.length > 0 && (
              <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
                <p className="text-sm font-semibold mb-1">New Users — Last 30 Days</p>
                <p className="text-dark-subtext text-xs mb-3">{growth.reduce((a, d) => a + d.users, 0)} signups</p>
                <Sparkline data={growth} color="#6C5CE7" label="users" />
                <div className="flex justify-between text-[10px] text-dark-subtext mt-1">
                  <span>{growth[0]?.date?.slice(5)}</span>
                  <span>{growth[growth.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
            )}
            {growth.length > 0 && (
              <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
                <p className="text-sm font-semibold mb-1">New Bookings — Last 30 Days</p>
                <p className="text-dark-subtext text-xs mb-3">{growth.reduce((a, d) => a + d.bookings, 0)} bookings</p>
                <Sparkline data={growth} color="#00b894" label="bookings" />
                <div className="flex justify-between text-[10px] text-dark-subtext mt-1">
                  <span>{growth[0]?.date?.slice(5)}</span>
                  <span>{growth[growth.length - 1]?.date?.slice(5)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── USERS ── */}
        {tab === 'Users' && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex gap-2">
              <input
                value={uSearch}
                onChange={e => { setUSearch(e.target.value); setUPage(1); }}
                placeholder="Search name or email…"
                className="flex-1 bg-dark-surface border border-dark-border rounded-xl px-3 py-2 text-sm text-dark-text placeholder-dark-subtext outline-none"
              />
              <select
                value={uType}
                onChange={e => { setUType(e.target.value); setUPage(1); }}
                className="bg-dark-surface border border-dark-border rounded-xl px-3 py-2 text-sm text-dark-text outline-none"
              >
                <option value="">All</option>
                <option value="CLIENT">Client</option>
                <option value="PROVIDER">Provider</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            <p className="text-dark-subtext text-xs">{uTotal} users</p>

            {uLoading ? (
              <div className="text-center py-10 text-dark-subtext text-sm">Loading…</div>
            ) : (
              <div className="space-y-2">
                {users.map(u => (
                  <div key={u.id} className="bg-dark-surface border border-dark-border rounded-2xl p-3 flex items-center gap-3">
                    <Avatar src={u.avatar} name={u.fullName} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-semibold text-dark-text truncate">{u.fullName}</p>
                        {!u.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">Banned</span>}
                        {u.isVerified && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">Verified</span>}
                      </div>
                      <p className="text-dark-subtext text-xs truncate">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TYPE_COLORS[u.accountType]}`}>{u.accountType}</span>
                        {u.location && <span className="text-dark-subtext text-[10px]">📍 {u.location}</span>}
                        <span className="text-dark-subtext text-[10px]">Joined {fmtDate(u.createdAt)}</span>
                      </div>
                      <div className="flex gap-3 mt-1">
                        {u.accountType === 'PROVIDER' && (
                          <>
                            <span className="text-dark-subtext text-[10px]">{u._count.services} services</span>
                            <span className="text-dark-subtext text-[10px]">{u._count.providerBookings} orders</span>
                            {u.rating > 0 && <span className="text-dark-subtext text-[10px]">⭐ {u.rating?.toFixed(1)}</span>}
                          </>
                        )}
                        {u.accountType === 'CLIENT' && (
                          <span className="text-dark-subtext text-[10px]">{u._count.clientBookings} bookings</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => toggleUserActive(u)}
                      className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-xl font-medium transition-colors ${
                        u.isActive
                          ? 'bg-red-500/10 text-red-400 active:bg-red-500/20'
                          : 'bg-green-500/10 text-green-400 active:bg-green-500/20'
                      }`}
                    >
                      {u.isActive ? 'Ban' : 'Unban'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Pagination page={uPage} pages={uPages} onPage={setUPage} />
          </div>
        )}

        {/* ── BOOKINGS ── */}
        {tab === 'Bookings' && (
          <div className="space-y-3">
            {/* Status filter */}
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
              {['', 'PENDING', 'ACTIVE', 'COMPLETED', 'CANCELLED', 'DISPUTED'].map(s => (
                <button
                  key={s}
                  onClick={() => { setBStatus(s); setBPage(1); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors border ${
                    bStatus === s
                      ? 'bg-brand-purple text-white border-brand-purple'
                      : 'bg-dark-surface text-dark-subtext border-dark-border'
                  }`}
                >
                  {s || 'All'}
                </button>
              ))}
            </div>

            <p className="text-dark-subtext text-xs">{bTotal} bookings</p>

            {bLoading ? (
              <div className="text-center py-10 text-dark-subtext text-sm">Loading…</div>
            ) : (
              <div className="space-y-2">
                {bookings.map(b => (
                  <div key={b.id} className="bg-dark-surface border border-dark-border rounded-2xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-dark-text truncate flex-1 mr-2">{b.service?.title}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                    </div>
                    <p className="text-xs text-dark-subtext">{CAT_LABELS[b.service?.category] || b.service?.category}</p>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <Avatar src={b.client?.avatar} name={b.client?.fullName} size={22} />
                        <span className="text-xs text-dark-subtext">{b.client?.fullName}</span>
                      </div>
                      <span className="text-dark-border">→</span>
                      <div className="flex items-center gap-1.5">
                        <Avatar src={b.provider?.avatar} name={b.provider?.fullName} size={22} />
                        <span className="text-xs text-dark-subtext">{b.provider?.fullName}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-dark-subtext">{fmtDate(b.createdAt)}</span>
                      <span className="text-sm font-bold text-dark-text">{formatPrice(b.totalAmount, currency)}</span>
                    </div>
                    {b.status === 'DISPUTED' && (
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => resolveBooking(b.id, 'COMPLETED')}
                          className="flex-1 py-2 rounded-xl bg-green-600/20 text-green-400 text-xs font-semibold">
                          Mark Complete
                        </button>
                        <button onClick={() => resolveBooking(b.id, 'CANCELLED')}
                          className="flex-1 py-2 rounded-xl bg-red-500/10 text-red-400 text-xs font-semibold">
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Pagination page={bPage} pages={bPages} onPage={setBPage} />
          </div>
        )}

        {/* ── SERVICES ── */}
        {tab === 'Services' && (
          <div className="space-y-3">
            <input
              value={sSearch}
              onChange={e => { setSSearch(e.target.value); setSPage(1); }}
              placeholder="Search services…"
              className="w-full bg-dark-surface border border-dark-border rounded-xl px-3 py-2 text-sm text-dark-text placeholder-dark-subtext outline-none"
            />

            <p className="text-dark-subtext text-xs">{sTotal} services</p>

            {sLoading ? (
              <div className="text-center py-10 text-dark-subtext text-sm">Loading…</div>
            ) : (
              <div className="space-y-2">
                {services.map(s => (
                  <div key={s.id} className="bg-dark-surface border border-dark-border rounded-2xl p-3 space-y-2">
                    <div className="flex items-start gap-3">
                      {s.images?.[0] ? (
                        <img src={s.images[0]} alt={s.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-dark-border flex items-center justify-center flex-shrink-0">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8E8E93" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21 15 16 10 5 21"/>
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-dark-text truncate">{s.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-dark-border text-dark-subtext">{CAT_LABELS[s.category] || s.category}</span>
                          {!s.isActive && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">Inactive</span>}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          <Avatar src={s.provider?.avatar} name={s.provider?.fullName} size={18} />
                          <span className="text-dark-subtext text-xs">{s.provider?.fullName}</span>
                        </div>
                      </div>
                      <button onClick={() => deleteService(s.id)}
                        className="flex-shrink-0 p-1.5 rounded-lg text-red-400 active:bg-red-500/10">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/>
                        </svg>
                      </button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-3">
                        <span className="text-dark-subtext text-[11px]">{s._count.bookings} bookings</span>
                        <span className="text-dark-subtext text-[11px]">{s._count.reviews} reviews</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleFeatured(s)}
                          className={`text-[10px] px-2 py-1 rounded-full font-medium transition-colors ${
                            s.isFeatured ? 'bg-yellow-500/20 text-yellow-400' : 'bg-dark-border text-dark-subtext'
                          }`}
                        >
                          {s.isFeatured ? '⭐ Featured' : 'Feature'}
                        </button>
                        <span className="text-sm font-bold text-dark-text">{formatPrice(s.price, currency)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Pagination page={sPage} pages={sPages} onPage={setSPage} />
          </div>
        )}

        {/* ── REPORTS ── */}
        {tab === 'Reports' && (
          <div className="space-y-3">
            <div className="flex gap-2">
              {['PENDING', 'RESOLVED', 'DISMISSED'].map(s => (
                <button key={s} onClick={() => setRStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                    rStatus === s ? 'bg-brand-purple text-white border-brand-purple' : 'bg-dark-surface text-dark-subtext border-dark-border'
                  }`}>{s}</button>
              ))}
            </div>
            {rLoading ? (
              <div className="text-center py-10 text-dark-subtext text-sm">Loading…</div>
            ) : reports.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-4xl mb-3">🛡️</p>
                <p className="text-dark-subtext text-sm">No {rStatus.toLowerCase()} reports</p>
              </div>
            ) : (
              <div className="space-y-2">
                {reports.map(r => (
                  <div key={r.id} className="bg-dark-surface border border-dark-border rounded-2xl p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        r.targetType === 'SERVICE' ? 'bg-blue-500/20 text-blue-400' : 'bg-orange-500/20 text-orange-400'
                      }`}>{r.targetType}</span>
                      <span className="text-dark-subtext text-[10px]">{fmtDate(r.createdAt)}</span>
                    </div>
                    <p className="text-sm font-semibold text-dark-text">{r.reason}</p>
                    {r.details && <p className="text-xs text-dark-subtext">{r.details}</p>}
                    <div className="flex items-center gap-2">
                      <Avatar src={r.reporter?.avatar} name={r.reporter?.fullName} size={18} />
                      <span className="text-xs text-dark-subtext">Reported by {r.reporter?.fullName}</span>
                    </div>
                    {r.status === 'PENDING' && (
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => resolveReport(r.id, 'RESOLVED')}
                          className="flex-1 py-2 rounded-xl bg-green-600/20 text-green-400 text-xs font-semibold">
                          Resolve
                        </button>
                        <button onClick={() => resolveReport(r.id, 'DISMISSED')}
                          className="flex-1 py-2 rounded-xl bg-dark-bg border border-dark-border text-dark-subtext text-xs font-semibold">
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      {/* ── Verify Tab ──────────────────────────────────────────────── */}
      {tab === 'Verify' && (
        <div className="px-4 pt-4">
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {['PENDING', 'APPROVED', 'REJECTED'].map(s => (
              <button key={s} onClick={() => setVStatus(s)}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold whitespace-nowrap ${
                  vStatus === s ? 'bg-brand-purple text-white border-brand-purple' : 'bg-dark-surface text-dark-subtext border-dark-border'
                }`}>{s.charAt(0) + s.slice(1).toLowerCase()}</button>
            ))}
          </div>
          {vLoading ? (
            <div className="flex flex-col gap-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-dark-surface rounded-2xl animate-pulse" />)}</div>
          ) : verifs.length === 0 ? (
            <div className="text-center py-12"><p className="text-dark-subtext text-sm">No {vStatus.toLowerCase()} verifications</p></div>
          ) : (
            <div className="flex flex-col gap-3">
              {verifs.map(v => (
                <div key={v.id} className="bg-dark-surface border border-dark-border rounded-2xl p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar src={v.avatar} name={v.fullName} size={36} />
                    <div className="flex-1 min-w-0">
                      <p className="text-dark-text text-sm font-semibold">{v.fullName}</p>
                      <p className="text-dark-subtext text-xs truncate">{v.email}</p>
                      <p className="text-dark-subtext text-xs">{v.location || 'No location'} · {v.accountType}</p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      v.verificationStatus === 'APPROVED' ? 'bg-green-500/20 text-green-400' :
                      v.verificationStatus === 'REJECTED' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>{v.verificationStatus}</span>
                  </div>
                  {v.idDocUrl && (
                    <a href={v.idDocUrl} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 px-3 py-2 bg-dark-bg border border-dark-border rounded-xl text-xs text-brand-purple font-medium">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      View ID Document
                    </a>
                  )}
                  {v.verificationStatus === 'PENDING' && (
                    <div className="flex gap-2">
                      <button onClick={() => handleVerif(v.id, 'APPROVED')}
                        className="flex-1 py-2 rounded-xl bg-green-600/20 text-green-400 text-xs font-semibold border border-green-600/30">
                        Approve
                      </button>
                      <button onClick={() => handleVerif(v.id, 'REJECTED')}
                        className="flex-1 py-2 rounded-xl bg-red-500/10 text-red-400 text-xs font-semibold border border-red-500/30">
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

        {/* ── WAITLIST ── */}
        {tab === 'Waitlist' && (
          <div className="space-y-3">
            {/* Filters */}
            <div className="flex gap-2">
              <input
                value={wSearch}
                onChange={e => { setWSearch(e.target.value); setWPage(1); }}
                placeholder="Search name or email…"
                className="flex-1 bg-dark-surface border border-dark-border rounded-xl px-3 py-2 text-sm text-dark-text placeholder-dark-subtext outline-none"
              />
              <select
                value={wType}
                onChange={e => { setWType(e.target.value); setWPage(1); }}
                className="bg-dark-surface border border-dark-border rounded-xl px-3 py-2 text-sm text-dark-text outline-none"
              >
                <option value="">All</option>
                <option value="CLIENT">Client</option>
                <option value="PROVIDER">Provider</option>
              </select>
            </div>

            <p className="text-dark-subtext text-xs">{wTotal} on waitlist</p>

            {wLoading ? (
              <div className="text-center py-10 text-dark-subtext text-sm">Loading…</div>
            ) : wEntries.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-4xl mb-3">📋</p>
                <p className="text-dark-subtext text-sm">No waitlist entries</p>
              </div>
            ) : (
              <div className="space-y-2">
                {wEntries.map((w, i) => (
                  <div key={w.id} className="bg-dark-surface border border-dark-border rounded-2xl p-3 flex items-center gap-3">
                    {/* Position number */}
                    <div className="w-9 h-9 rounded-full bg-brand-purple/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-brand-purple text-xs font-bold">#{(wPage - 1) * 20 + i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-dark-text truncate">{w.fullName}</p>
                      <p className="text-dark-subtext text-xs truncate">{w.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          w.type === 'PROVIDER' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                        }`}>{w.type}</span>
                        <span className="text-dark-subtext text-[10px]">{fmtDate(w.createdAt)}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromWaitlist(w.id)}
                      className="flex-shrink-0 p-1.5 rounded-lg text-red-400 active:bg-red-500/10"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <Pagination page={wPage} pages={wPages} onPage={setWPage} />
          </div>
        )}

      </div>
    </div>
  );
}
